import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CompletePipelineStatus, OverallPipelineState } from '../types/airflow';
import { getCompletePipelineStatus } from '../utils/airflowApi';
import { queryKeys } from '../lib/queryClient';

interface UseCompletePipelineStatusOptions {
  date: string;
  onStatusChange?: (status: CompletePipelineStatus) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const useCompletePipelineStatus = ({
  date,
  onStatusChange,
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds default
}: UseCompletePipelineStatusOptions) => {
  // State to control polling
  const [shouldPoll, setShouldPoll] = React.useState(true);
  
  const query = useQuery<CompletePipelineStatus>({
    queryKey: queryKeys.completePipelineStatus(date),
    queryFn: () => getCompletePipelineStatus(date),
    enabled: Boolean(date),
    // Poll every 30 seconds when pipeline is not in final state
    refetchInterval: autoRefresh && shouldPoll ? refreshInterval : false,
    // Disable automatic refetch on window focus
    refetchOnWindowFocus: false,
    // Don't retry on errors - just wait for next interval
    retry: false,
    // Keep previous data while refetching
    placeholderData: (previousData) => previousData,
    // Consider data fresh for 25 seconds
    staleTime: 25000,
  });

  // Call status change callback when data changes
  React.useEffect(() => {
    if (query.data && onStatusChange) {
      onStatusChange(query.data);
    }
  }, [query.data, onStatusChange]);

  // Stop polling when pipeline reaches final state
  React.useEffect(() => {
    if (query.data?.overall_state && isInFinalState(query.data.overall_state)) {
      setShouldPoll(false);
    } else if (query.data?.overall_state && !isInFinalState(query.data.overall_state)) {
      setShouldPoll(true);
    }
  }, [query.data?.overall_state]);

  const isWaitingState = query.data?.overall_state === 'waiting';

  return {
    ...query,
    status: query.data as CompletePipelineStatus | undefined,
    isPolling: (shouldPoll || isWaitingState) && autoRefresh && !query.isPaused,
    // Helper methods for common status checks
    isRunning: query.data?.overall_state === OverallPipelineState.RUNNING,
    isCompleted: isInFinalState(query.data?.overall_state),
    isSuccessful: query.data?.overall_state === OverallPipelineState.SUCCESS,
    hasFailed: query.data?.overall_state === OverallPipelineState.FAILED,
    hasPartialSuccess: query.data?.overall_state === OverallPipelineState.PARTIAL_SUCCESS,
  };
};

// Helper function to check if pipeline is in final state
const isInFinalState = (state?: string) => {
  if (!state) return false;
  
  const finalStates = [
    OverallPipelineState.SUCCESS,
    OverallPipelineState.FAILED,
    OverallPipelineState.PARTIAL_SUCCESS,
  ];
  return finalStates.includes(state as OverallPipelineState);
};
